'use client';

import { useEffect } from 'react';

const RELOAD_KEY = 'aifit-chunk-reload';

/**
 * After a Vercel deploy, a PWA still holding an old shell can request a JS chunk
 * that no longer exists → "Loading chunk N failed". One hard reload usually fixes it.
 */
export default function ChunkLoadRecovery() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const reloadOnce = () => {
      try {
        if (sessionStorage.getItem(RELOAD_KEY) === '1') return;
        sessionStorage.setItem(RELOAD_KEY, '1');
      } catch {
        // sessionStorage blocked — still attempt a single reload.
      }
      window.location.reload();
    };

    const onError = (event) => {
      const msg = String(event?.message || event?.reason || '');
      if (/Loading chunk [\w-]+ failed/i.test(msg) || /ChunkLoadError/i.test(msg)) {
        reloadOnce();
      }
    };

    const onRejection = (event) => {
      const reason = event?.reason;
      const msg = String(reason?.message || reason || '');
      if (
        reason?.name === 'ChunkLoadError' ||
        /Loading chunk [\w-]+ failed/i.test(msg) ||
        /ChunkLoadError/i.test(msg)
      ) {
        reloadOnce();
      }
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    // Clear the guard after a healthy load so a later deploy can recover again.
    try {
      if (sessionStorage.getItem(RELOAD_KEY) === '1') {
        window.setTimeout(() => {
          try {
            sessionStorage.removeItem(RELOAD_KEY);
          } catch {
            /* ignore */
          }
        }, 8000);
      }
    } catch {
      /* ignore */
    }

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
